/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useMemo } from "react";
import { useAuth } from "@context/authentication";
import { useAgency } from "@pages/authenticated/agency-pages/AgenciesContext";
import {
  isAdmin,
  isOperationalOrHigher,
  isBrokerOrHigher,
  hasPermission,
  getCurrentUserRole,
} from "@/shared/utils/permissions";

/**
 * Hook to check agency permissions for the current user
 * @returns Object with permission flags and helper functions
 */
export const useAgencyPermissions = () => {
  const auth = useAuth();
  const { agency } = useAgency((state) => state);

  const userEmail = useMemo(() => {
    return (auth.user?.profile as { email?: string })?.email || null;
  }, [auth.user]);

  const currentUserRole = useMemo(() => {
    return getCurrentUserRole(agency, userEmail);
  }, [agency, userEmail]);

  // Role checks
  const admin = useMemo(() => {
    return isAdmin(currentUserRole);
  }, [currentUserRole]);

  const isOperational = useMemo(() => {
    return isOperationalOrHigher(currentUserRole);
  }, [currentUserRole]);

  const isBroker = useMemo(() => {
    return isBrokerOrHigher(currentUserRole);
  }, [currentUserRole]);

  // Agency permissions
  const canEditAgency = useMemo(() => {
    return hasPermission("edit_agency", currentUserRole);
  }, [currentUserRole]);

  const canCreateAgency = useMemo(() => {
    return hasPermission("create_agency", currentUserRole);
  }, [currentUserRole]);

  const canEditPlanAgency = useMemo(() => {
    return hasPermission("edit_plan_agency", currentUserRole);
  }, [currentUserRole]);

  const canViewAgency = useMemo(() => {
    return hasPermission("view_agency", currentUserRole);
  }, [currentUserRole]);

  const canViewAgencySettings = useMemo(() => {
    return hasPermission("view_agency_settings", currentUserRole);
  }, [currentUserRole]);

  const canManageAgency = useMemo(() => {
    return hasPermission("manage_agency", currentUserRole);
  }, [currentUserRole]);

  const canInviteMember = useMemo(() => {
    return hasPermission("invite_agency_member", currentUserRole);
  }, [currentUserRole]);

  // Member permissions
  const canEditMember = useMemo(() => {
    return hasPermission("edit_member", currentUserRole);
  }, [currentUserRole]);

  const canEditMemberRole = useMemo(() => {
    return hasPermission("edit_member_role", currentUserRole);
  }, [currentUserRole]);

  // Sale permissions
  const canManageSalePaperworkMilestones = useMemo(() => {
    return hasPermission("manage_sale", currentUserRole);
  }, [currentUserRole]);

  // Lease permissions
  const canManageLeaseSettings = useMemo(() => {
    return hasPermission("manage_rent", currentUserRole);
  }, [currentUserRole]);

  // Property settings permissions
  const canManagePropertySettings = useMemo(() => {
    return hasPermission("manage_property_settings", currentUserRole);
  }, [currentUserRole]);

  // Client permissions
  const canAddClient = useMemo(() => {
    return hasPermission("add_client", currentUserRole);
  }, [currentUserRole]);

  // Property permissions
  const canAddProperty = useMemo(() => {
    return hasPermission("add_property", currentUserRole);
  }, [currentUserRole]);

  const canEditProperty = useMemo(() => {
    return hasPermission("edit_property", currentUserRole);
  }, [currentUserRole]);

  const canDeleteProperty = useMemo(() => {
    return hasPermission("delete_property", currentUserRole);
  }, [currentUserRole]);

  const canViewProperty = useMemo(() => {
    return hasPermission("view_property", currentUserRole);
  }, [currentUserRole]);

  // Image permissions
  const canAddImage = useMemo(() => {
    return hasPermission("add_image", currentUserRole);
  }, [currentUserRole]);

  // Reports permissions
  const canViewDashboards = useMemo(() => {
    return hasPermission("view_dashboards", currentUserRole);
  }, [currentUserRole]);

  // Visit permissions
  const canAddVisit = useMemo(() => {
    return hasPermission("add_visit", currentUserRole);
  }, [currentUserRole]);

  const canEditVisit = useMemo(() => {
    return hasPermission("edit_visit", currentUserRole);
  }, [currentUserRole]);

  const canViewVisits = useMemo(() => {
    return hasPermission("view_visits", currentUserRole);
  }, [currentUserRole]);

  const canDeleteVisit = useMemo(() => {
    return hasPermission("delete_visit", currentUserRole);
  }, [currentUserRole]);

  const canManageVisits = useMemo(() => {
    return hasPermission("manage_visits", currentUserRole);
  }, [currentUserRole]);

  return {
    // Role flags
    isAdmin: admin,
    isOperational,
    isBroker,
    currentUserRole,
    userEmail,
    // Agency permissions
    canEditAgency,
    canCreateAgency,
    canEditPlanAgency,
    canViewAgency,
    canViewAgencySettings,
    canManageAgency,
    canInviteMember,
    // Member permissions
    canEditMember,
    canEditMemberRole,
    // Sale permissions
    canManageSalePaperworkMilestones,
    canManageLeaseSettings,
    canManagePropertySettings,
    // Client permissions
    canAddClient,
    // Property permissions
    canAddProperty,
    canEditProperty,
    canDeleteProperty,
    canViewProperty,
    // Image permissions
    canAddImage,
    // Reports permissions
    canViewDashboards,
    // Visit permissions
    canAddVisit,
    canEditVisit,
    canViewVisits,
    canDeleteVisit,
    canManageVisits,
  };
};
